/**
 * FormSelect
 * ----------
 * Polaris Select dropdown connected to React Hook Form.
 */

import { Select, type SelectProps } from "@shopify/polaris";
import { useController, useFormContext, type FieldValues, type Path } from "react-hook-form";
import { useVariantFieldDisabled } from "./context/VariantFieldContext";

export interface FormSelectProps<TFieldValues extends FieldValues = FieldValues> extends Omit<
    SelectProps,
    "value" | "onChange" | "error"
> {
    name: Path<TFieldValues> | (string & NonNullable<unknown>);
    disabled?: boolean;
    /** Override the value read from RHF (e.g. for array-item fields not registered as a real path). */
    value?: string;
    /** Override the write to RHF — receives the raw new value + the underlying field. */
    onChange?: (value: string, field: ReturnType<typeof useController<TFieldValues>>["field"]) => void;
    /** Override the error shown (e.g. for custom local-state validation instead of/in addition to schema validation). */
    error?: string | boolean;
}

export function FormSelect<TFieldValues extends FieldValues = FieldValues>({
    name,
    disabled,
    value,
    onChange,
    error,
    ...rest
}: FormSelectProps<TFieldValues>) {
    const { control } = useFormContext<TFieldValues>();

    const {
        field,
        fieldState: { error: fieldError },
    } = useController<TFieldValues>({ control, name: name as any });

    const isAbDisabled = useVariantFieldDisabled(name as string);

    return (
        <Select
            {...rest}
            value={value !== undefined ? value : String(field.value ?? "")}
            onChange={(val) => (onChange ? onChange(val, field) : field.onChange(val))}
            error={error !== undefined ? error : fieldError?.message}
            disabled={disabled || isAbDisabled}
        />
    );
}
